import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "./api";

const serviceTypes = ["AMC", "Repair", "Installation", "Generator", "Panels", "Supply"];
const projectStatuses = ["Planning", "Mobilization", "In Progress", "On Hold", "Completed", "Closed", "Cancelled"];
const priorities = ["Normal", "Urgent", "Critical"];
const paymentStatuses = ["Not Started", "Advance Pending", "Part Paid", "Fully Paid", "Overdue"];
const documentCategories = ["LPO received", "LPO issued", "PFI", "Site Survey pictures", "Completed Work"];
const clientValueUsers = new Set([
  "kandarp.g@globusafriventures.com",
  "radhey.g@globusafriventures.com",
  "paschal.a@globusafriventures.com"
]);

function ClientProjectDetail({ user }) {
  const { id, projectId } = useParams();
  const [project, setProject] = useState(null);
  const [draft, setDraft] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [files, setFiles] = useState({});
  const [saveStatus, setSaveStatus] = useState("");
  const canViewValues = clientValueUsers.has((user?.email || "").toLowerCase());

  function loadProject() {
    apiFetch(`/api/projects/${projectId}`)
      .then(res => res.json())
      .then(data => {
        setProject(data);
        setDraft(data);
      });
  }

  function loadDocuments() {
    apiFetch(`/api/projects/${projectId}/documents`)
      .then(res => res.json())
      .then(data => setDocuments(Array.isArray(data) ? data : []));
  }

  useEffect(() => {
    loadProject();
    loadDocuments();
  }, [projectId]);

  function updateDraft(field, value) {
    setDraft(prev => ({ ...prev, [field]: value }));
  }

  function saveProject() {
    apiFetch(`/api/projects/${projectId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft)
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(data.error);
          return;
        }
        setSaveStatus("Saved");
        loadProject();
        setTimeout(() => setSaveStatus(""), 1500);
      });
  }

  function upload(category) {
    const file = files[category];
    if (!file) {
      alert("Select a file first");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    const params = new URLSearchParams({ category });
    apiFetch(`/api/projects/${projectId}/documents/upload?${params}`, {
      method: "POST",
      body: formData
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(data.error);
          return;
        }
        setFiles(prev => ({ ...prev, [category]: null }));
        loadDocuments();
      });
  }

  function openDocument(documentId) {
    apiFetch(`/api/client-documents/${documentId}/download`)
      .then(res => res.blob())
      .then(blob => window.open(window.URL.createObjectURL(blob)));
  }

  if (!project || !draft) return <div style={page}>Loading...</div>;

  return (
    <div style={page}>
      <button onClick={() => (window.location.href = `/clients/${id}/projects`)}>Back to Projects</button>
      <h2>{project.project_name || "Project Details"}</h2>
      {project.tracking_id && <p style={muted}>ID: {project.tracking_id}</p>}

      <div style={card}>
        <h3>Project Information</h3>
        <ProjectForm draft={draft} updateDraft={updateDraft} canViewValues={canViewValues} />
        <button onClick={saveProject}>Save Project Details</button>
        {saveStatus && <span style={successText}>{saveStatus}</span>}
      </div>

      <div style={card}>
        <h3>Project Documents & Pictures</h3>
        <div style={documentGrid}>
          {documentCategories.map(category => (
            <div key={category} style={uploadCard}>
              <h4>{category}</h4>
              {category === "Site Survey pictures" ? (
                <p style={muted}>Displayed from the sales site survey upload.</p>
              ) : (
                <>
                  <input type="file" onChange={e => setFiles(prev => ({ ...prev, [category]: e.target.files?.[0] || null }))} />
                  <button onClick={() => upload(category)}>Upload</button>
                </>
              )}
              <ul style={documentList}>
                {documents
                  .filter(doc => doc.category === category)
                  .map(doc => (
                    <li key={doc.id}>
                      <button onClick={() => openDocument(doc.id)}>Open</button>
                      <span>{doc.original_filename}</span>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProjectForm({ draft, updateDraft, canViewValues }) {
  return (
    <div style={formGrid}>
      <Field label="Project Name">
        <input placeholder="Project name" value={draft.project_name || ""} onChange={e => updateDraft("project_name", e.target.value)} />
      </Field>
      <Field label="Service Type">
        <select value={draft.service_type || "AMC"} onChange={e => updateDraft("service_type", e.target.value)}>
          {serviceTypes.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
      </Field>
      <Field label="Site Address">
        <input placeholder="Site address" value={draft.site_address || ""} onChange={e => updateDraft("site_address", e.target.value)} />
      </Field>
      <Field label="Scope / Work Description">
        <textarea placeholder="Scope / work description" value={draft.scope_description || ""} onChange={e => updateDraft("scope_description", e.target.value)} />
      </Field>
      {canViewValues && (
        <Field label="Project Value">
          <input placeholder="Project value" value={draft.project_value || ""} onChange={e => updateDraft("project_value", e.target.value)} />
        </Field>
      )}
      <Field label="Start Date">
        <input type="date" value={draft.start_date || ""} onChange={e => updateDraft("start_date", e.target.value)} />
      </Field>
      <Field label="Expected Completion Date">
        <input type="date" value={draft.expected_completion_date || ""} onChange={e => updateDraft("expected_completion_date", e.target.value)} />
      </Field>
      <Field label="Actual Completion Date">
        <input type="date" value={draft.actual_completion_date || ""} onChange={e => updateDraft("actual_completion_date", e.target.value)} />
      </Field>
      <Field label="Project Status">
        <select value={draft.status || "Planning"} onChange={e => updateDraft("status", e.target.value)}>
          {projectStatuses.map(status => <option key={status} value={status}>{status}</option>)}
        </select>
      </Field>
      <Field label="Project Manager">
        <input placeholder="Project manager" value={draft.project_manager || ""} onChange={e => updateDraft("project_manager", e.target.value)} />
      </Field>
      <Field label="Assigned Engineer">
        <input placeholder="Assigned engineer" value={draft.assigned_engineer || ""} onChange={e => updateDraft("assigned_engineer", e.target.value)} />
      </Field>
      <Field label="Vendor / SBC">
        <input placeholder="Vendor / SBC" value={draft.vendor_or_sbc || ""} onChange={e => updateDraft("vendor_or_sbc", e.target.value)} />
      </Field>
      <Field label="Priority">
        <select value={draft.priority || "Normal"} onChange={e => updateDraft("priority", e.target.value)}>
          {priorities.map(priority => <option key={priority} value={priority}>{priority}</option>)}
        </select>
      </Field>
      <Field label="Payment Status">
        <select value={draft.payment_status || "Not Started"} onChange={e => updateDraft("payment_status", e.target.value)}>
          {paymentStatuses.map(status => <option key={status} value={status}>{status}</option>)}
        </select>
      </Field>
      <Field label="Current Stage / Milestone">
        <input placeholder="Current stage / milestone" value={draft.current_stage || ""} onChange={e => updateDraft("current_stage", e.target.value)} />
      </Field>
      <Field label="Next Action">
        <textarea placeholder="Next action" value={draft.next_action || ""} onChange={e => updateDraft("next_action", e.target.value)} />
      </Field>
      <Field label="Remarks">
        <textarea placeholder="Remarks" value={draft.remarks || ""} onChange={e => updateDraft("remarks", e.target.value)} />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={fieldLabel}>
      <span>{label}</span>
      {children}
    </label>
  );
}

const page = { padding: "20px" };
const card = { border: "1px solid #ddd", padding: "15px", margin: "20px 0", borderRadius: "8px", backgroundColor: "#f9f9f9" };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px", marginBottom: "12px" };
const fieldLabel = { display: "grid", gap: "4px", fontSize: "14px", fontWeight: "bold", textAlign: "left" };
const documentGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "12px" };
const uploadCard = { display: "grid", gap: "8px", border: "1px solid #ddd", borderRadius: "8px", padding: "10px", backgroundColor: "#fff" };
const documentList = { margin: 0, paddingLeft: "18px", textAlign: "left" };
const successText = { color: "green", fontWeight: "bold", marginLeft: "10px" };
const muted = { color: "#666", marginTop: 0 };

export default ClientProjectDetail;
